/**
 * A generic class to handle interaction between a trigger (btn) that open a popover/popup element
 */
export class PopTrigger {
    private readonly triggerSelector = '.js-pop-trigger'
    private readonly attributeTargetName = 'data-pop-trigger-target-selector';
    private readonly attributeCustomActiveClass = 'data-pop-trigger-active-class';
    private readonly attributeCustomAction = 'data-pop-trigger-custom-action';
    private readonly activeClass = 'is-active';
    private readonly customActions: PopTriggerCustomAction

    private readonly $triggerList: HTMLElement[];
    constructor(customActions?: PopTriggerCustomAction) {
        this.customActions = customActions || {};
        this.$triggerList = Array.from(document.querySelectorAll(this.triggerSelector));

        document.addEventListener('click', ev => {
            const $triggerElem = (ev.target as HTMLElement).closest(this.triggerSelector)
            if (!$triggerElem) {
                return;
            }

            this.onTrigger($triggerElem as HTMLElement);
            ev.preventDefault();
        })
    }
    /**
     * User clicks a trigger
     * @private
     * @param $trigger
     */
    private onTrigger($trigger: HTMLElement) {
        const targetSelector = $trigger.getAttribute(this.attributeTargetName);

        if (!targetSelector) {
            return;
        }

        const $target: HTMLElement|null = document.querySelector(targetSelector);

        if (!$target) {
            return;
        }

        const customActions = this.getCustomActions($trigger);

        const activeClass = $trigger.getAttribute(this.attributeCustomActiveClass) || this.activeClass;

        const state = this.toggleSameTriggers($trigger, activeClass, targetSelector);

        if (state) {
            this.callCustomActions(customActions, PopTriggerCallableName.BEFORE_ACTIVE, $trigger, $target);
        }
        $target.classList.toggle(activeClass, state);

        if (!state) {
            this.callCustomActions(customActions, PopTriggerCallableName.DIRECTLY_AFTER_HIDDEN, $trigger, $target);
        }

        $target.addEventListener('transitionend', ev => {
            if (!state) {
                this.callCustomActions(customActions, PopTriggerCallableName.AFTER_HIDDEN, $trigger, $target);
            } else {
                this.callCustomActions(customActions, PopTriggerCallableName.AFTER_ACTIVE, $trigger, $target);
            }
        }, { once: true });
    }

    /**
     * Toggle class for the same triggers
     * @param $trigger
     * @param activeClass
     * @param targetSelector
     * @return boolean whether the class is applied
     * @private
     */
    private toggleSameTriggers($trigger: HTMLElement, activeClass: string, targetSelector: string): boolean {
        const state: boolean = $trigger.classList.toggle(activeClass);
        const sameTriggers = this.$triggerList.filter($trigger => $trigger.getAttribute(this.attributeTargetName) === targetSelector);

        sameTriggers.forEach(function($trigger) {
            $trigger.classList.toggle(activeClass, state);
        });

        return state;
    }

    /**
     * Whether a trigger element have custom action attached to it
     * @param $trigger
     * @private
     */
    private getCustomActions($trigger: HTMLElement): PopTriggerCallable[]  {
        return Object.keys(this.customActions)
                .filter(customAction => $trigger.getAttribute(this.attributeCustomAction) === customAction)
                .map(customAction => this.customActions[customAction]);
    }

    private callCustomActions(customActions: PopTriggerCallable[],
                              callableName: PopTriggerCallableName,
                              $trigger: HTMLElement,
                              $target: HTMLElement
    ): void {
        if (customActions.length === 0) {
            return;
        }

        customActions.forEach(call => {
            if (!call[callableName]) {
                return;
            }

            call[callableName]?.($trigger, $target);
        })
    }
}

declare type PopTriggerCustomAction =
        Record<
                string,
                PopTriggerCallable
        >

export interface PopTriggerCallable {
    [PopTriggerCallableName.BEFORE_ACTIVE]?: ($trigger: HTMLElement, $target: HTMLElement) => void,
    [PopTriggerCallableName.AFTER_HIDDEN]?: ($trigger: HTMLElement, $target: HTMLElement) => void,
    [PopTriggerCallableName.AFTER_ACTIVE]?: ($trigger: HTMLElement, $target: HTMLElement) => void,
    [PopTriggerCallableName.DIRECTLY_AFTER_HIDDEN]?: ($trigger: HTMLElement, $target: HTMLElement) => void,
}

export enum PopTriggerCallableName {
    BEFORE_ACTIVE = 'beforeActive',
    AFTER_HIDDEN = 'afterHidden',
    DIRECTLY_AFTER_HIDDEN = 'directlyAfterHidden',
    AFTER_ACTIVE = 'afterAction',
}